import { MouseEvent } from 'react';
import { SecondaryButton } from '@ardoq/button';
import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { FlexBox, Stack } from '@ardoq/layout';
import { EmptyStatesDoqContainer } from '@ardoq/graph';
import { clearPerspectiveEditor } from '../../../perspective/actions';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import { perspectiveWindowManager } from 'perspective/perspectiveEditor/perspectiveWindowManager';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { t, Trans, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

type NoDataWithFiltersDoqProps = {
  doqType: DoqType;
  includeActions?: boolean;
  canApplyIncludeAllDescendantsFilter?: boolean;
  i18nLocale: I18nLocale;
};

const NoDataWithFiltersDoqBase = ({
  doqType,
  includeActions = true,
  canApplyIncludeAllDescendantsFilter = false,
  i18nLocale,
}: NoDataWithFiltersDoqProps) => {
  const onOpenPerspectivesClick = (event: MouseEvent) => {
    perspectiveWindowManager.open({
      event,
      focusPerspectiveName: false,
    });
  };
  return (
    <EmptyStatesDoqContainer
      data-testid={`${doqType}-emptystate__no-data-with-filters`}
    >
      <ResponsiveDoqWithSpeechBubble
        doqType={doqType}
        title={t(
          i18nLocale,
          'tabview:graphViews.emptyState.noDataWithFilters.title',
          'There is no data to show'
        )}
        message={
          <Stack gap="xlarge">
            {canApplyIncludeAllDescendantsFilter ? (
              <FlexBox gap="medium">
                <span style={{ display: 'inline' }}>
                  <Trans
                    i18nKey="tabview:graphViews.emptyState.noDataWithFilters.messageWithDescendants"
                    defaults='To show data in this view, try activating view modifier "<icon/>" to show descendants or modify the filter settings in Perspectives.'
                    components={{
                      icon: (
                        <Icon
                          iconName={IconName.BLUR_ON}
                          iconSize={IconSize.SMALL}
                        />
                      ),
                    }}
                  />
                </span>
              </FlexBox>
            ) : (
              <FlexBox gap="medium">
                {t(
                  i18nLocale,
                  'tabview:graphViews.emptyState.noDataWithFilters.message',
                  'To show more data, clear filters or modify the settings in Perspectives.'
                )}
              </FlexBox>
            )}
          </Stack>
        }
        buttonLabel={
          includeActions
            ? t(
                i18nLocale,
                'tabview:graphViews.emptyState.noDataWithFilters.clearFilters',
                'Clear filters'
              )
            : undefined
        }
        onButtonClick={
          includeActions
            ? () => {
                dispatchAction(clearPerspectiveEditor());
              }
            : undefined
        }
        extraButton={
          includeActions ? (
            <SecondaryButton onClick={onOpenPerspectivesClick}>
              {t(
                i18nLocale,
                'tabview:graphViews.emptyState.noDataWithFilters.openPerspectives',
                'Open Perspectives'
              )}
            </SecondaryButton>
          ) : undefined
        }
      />
    </EmptyStatesDoqContainer>
  );
};
export const NoDataWithFiltersDoq = connect(
  NoDataWithFiltersDoqBase,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);
